import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { genericNamesApi, type NamedMasterDto } from "@/api/endpoints";

interface Props {
  value: NamedMasterDto | null;
  /** When the parent has only an id (editing), the picker resolves it to a row. */
  initialId?: string | null;
  onChange: (v: NamedMasterDto | null) => void;
  label?: string;
  disabled?: boolean;
  helperText?: string;
}

function useDebouncedValue(value: string, delay = 250) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

/**
 * Lookup for the medicine generic-name master (Feedback batch 1, Code 1). Selection-only —
 * new generics are added under Catalog → Generic Names.
 */
export function GenericNamePicker({
  value, initialId, onChange, label = "Generic name", disabled, helperText,
}: Props) {
  // `input` is what the textbox shows — MUI also rewrites it to the selected option's label
  // (reason "reset"). `search` is only what the user typed, so a reset label never becomes a
  // query that filters the current value out of the option list.
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search);

  const q = useQuery({
    queryKey: ["generic-names", "picker", debounced],
    queryFn: () => genericNamesApi.list({ search: debounced || undefined, pageSize: 50 }),
    staleTime: 60_000,
  });

  // Resolve initialId to a row so the field shows the current value when editing. Fetched by
  // id: scanning the first page of the master missed every generic that sorted past it, which
  // left an item whose generic *is* mapped showing an empty field.
  const resolveQuery = useQuery({
    queryKey: ["generic-names", "detail", initialId],
    queryFn: () => genericNamesApi.get(initialId!),
    enabled: !value && !!initialId,
    staleTime: 60_000,
  });
  const resolved = value ?? (initialId ? resolveQuery.data ?? null : null);

  // The resolved row need not be on the current search page; keep it in options so MUI doesn't
  // treat the value as not being one of the options.
  const options = q.data?.items ?? [];
  const withValue = resolved && !options.some(o => o.id === resolved.id)
    ? [resolved, ...options]
    : options;

  return (
    <Autocomplete<NamedMasterDto>
      value={resolved}
      options={withValue}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        setInput(v);
        setSearch(reason === "input" ? v : "");
      }}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
